import React, { useState, useEffect, useRef } from "react";
import Icon from "@mdi/react";
import {
  mdiAlert,
  mdiDotsHorizontal,
  mdiCpu64Bit,
  mdiChevronRight,
  mdiChevronDown,
  mdiEye,
  mdiUndoVariant,
  mdiFileCheckOutline,
  mdiBlockHelper,
} from "@mdi/js";
import { DisputedItemModel } from "../model/quarantine_model";
import dayjs from "dayjs";
import QuarantineItemDetailsModal from "./QuarantineItemDetailsModal";
import DeviceIdsViewer from "../common-components/DeviceIdsViewer";
import { MobileCard, MobileField } from "../common-components/MobileCard";
import { UpdateDisputeStatusApi } from "../warehouse_module_repo";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";

interface QuarantinedItemsListProps {
  items: DisputedItemModel[];
  onRefresh: () => Promise<void>;
}

const QuarantinedItemsList: React.FC<QuarantinedItemsListProps> = ({
  items,
  onRefresh,
}) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const [selectedItemForDetails, setSelectedItemForDetails] =
    useState<DisputedItemModel | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const { showToast } = useToast();

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // If the click is outside the dropdown AND not on the action button itself
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        !target.closest(".action-btn")
      ) {
        setActiveDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const statusStyles: Record<string, string> = {
    open: "bg-yellow-100 text-yellow-700 border-yellow-200",
    returned: "bg-blue-100 text-blue-700 border-blue-200",
    released: "bg-green-100 text-green-700 border-green-200",
    resolved: "bg-green-100 text-green-700 border-green-200",
    scrapped: "bg-red-100 text-red-700 border-red-200",
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleActionClick = async (action: string, item: DisputedItemModel) => {
    setActiveDropdownId(null);

    if (action === "view") {
      setSelectedItemForDetails(item);
      setIsDetailsModalOpen(true);
      return;
    }

    const actionConfig: Record<
      string,
      { status: string; reason: string; successText: string }
    > = {
      return: {
        status: "returned",
        reason: "Returning to vendor",
        successText: "Item marked as returned successfully",
      },
      release: {
        status: "released",
        reason: "Releasing to stock",
        successText: "Item released to stock successfully",
      },
      scrap: {
        status: "scrapped",
        reason: "Scrapping quarantined item",
        successText: "Item marked as scrapped successfully",
      },
    };

    const config = actionConfig[action];
    if (!config) return;

    const res = await UpdateDisputeStatusApi({
      status: config.status,
      reason: config.reason,
      dispute_id: item.id ?? "",
    });

    if (!res.success) {
      showToast({
        text: "Failed to update status: " + res.error,
        type: ToastType.error,
      });
      return;
    }

    showToast({
      type: ToastType.success,
      text: config.successText,
    });

    await onRefresh();
  };

  return (
    <div
      id="warehouse-quarantine-items-list-root"
      className="rounded-lg border bg-white text-card-foreground shadow-sm min-h-[500px] flex flex-col items-stretch"
    >
      <div className="flex flex-col space-y-1.5 p-6 pb-3">
        <h3 className="tracking-tight text-base font-semibold flex items-center gap-2">
          <Icon path={mdiAlert} size={0.8} className="text-yellow-500" />
          Quarantined Items
        </h3>
      </div>
      <div className="flex-1 overflow-x-auto hidden md:block">
        <table className="w-full caption-bottom text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="border-b transition-colors hover:bg-muted/50">
              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-8"></th>
              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground whitespace-nowrap">
                Item
              </th>
              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                Order
              </th>
              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground hidden md:table-cell">
                Warehouse
              </th>
              <th className="h-12 px-4 align-middle font-medium text-muted-foreground text-right">
                Disputed Qty
              </th>
              <th className="h-12 px-4 align-middle font-medium text-muted-foreground text-right">
                Disputed Rate
              </th>
              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                Dispute Type
              </th>
              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                Status
              </th>
              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground hidden md:table-cell">
                Date
              </th>
              <th className="h-12 px-4 align-middle font-medium text-muted-foreground text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="[&_tr:last-child]:border-0 divide-y">
            {items.map((item, index) => {
              const isFG = item.item_type === "Finished Goods";
              const itemId = item.id || `item-${index}`;
              const isExpanded = expandedRows[itemId];
              const isDropdownOpen = activeDropdownId === itemId;

              return (
                <React.Fragment key={itemId}>
                  <tr className="border-b transition-colors hover:bg-gray-50 group">
                    <td className="p-4 align-middle leading-none">
                      {isFG && (
                        <button
                          id={`warehouse-quarantine-item-row-${itemId}-expand-button`}
                          onClick={() => toggleRow(itemId)}
                          className="inline-flex items-center justify-center rounded-md hover:bg-gray-200 h-6 w-6 transition-colors"
                          aria-label="Expand device IDs"
                        >
                          <Icon
                            path={isExpanded ? mdiChevronDown : mdiChevronRight}
                            size={0.8}
                          />
                        </button>
                      )}
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="font-medium text-gray-900">
                            {item.item_name || "N/A"}
                          </p>
                          <p className="text-sm text-gray-500">
                            {item.inventory_item_code}
                          </p>
                        </div>
                        {isFG && (
                          <div className="inline-flex items-center rounded-full border border-gray-200 px-2 py-0.5 text-xs font-semibold gap-1 bg-gray-50">
                            <Icon path={mdiCpu64Bit} size={0.5} />
                            FG
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4 align-middle font-mono text-xs text-gray-600">
                      {item.inward_order_id}
                    </td>
                    <td className="p-4 align-middle hidden md:table-cell text-gray-500">
                      {item.warehouse_name}
                    </td>
                    <td className="p-4 align-middle text-right font-semibold">
                      {item.disputed_physical_quantity}
                    </td>
                    <td className="p-4 align-middle text-right font-semibold">
                      {formatCurrency(item.disputed_invoice_rate || 0)}
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex flex-wrap gap-1">
                        {item.dispute_types.map((type, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-700"
                          >
                            {type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-4 align-middle">
                      <span
                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                          statusStyles[item.status?.toLowerCase()] ||
                          "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {item.status?.charAt(0).toUpperCase() +
                          item.status?.slice(1)}
                      </span>
                    </td>
                    <td className="p-4 align-middle hidden md:table-cell text-gray-500 whitespace-nowrap">
                      {item.created_at
                        ? dayjs(item.created_at).format("DD MMM YYYY")
                        : "N/A"}
                    </td>
                    <td className="p-4 align-middle text-right relative">
                      <button
                        id={`warehouse-quarantine-item-row-${itemId}-action-menu-button`}
                        className="action-btn text-gray-500 hover:text-gray-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdownId(isDropdownOpen ? null : itemId);
                        }}
                        type="button"
                      >
                        <Icon path={mdiDotsHorizontal} size={0.8} />
                      </button>

                      {isDropdownOpen && (
                        <div
                          id={`warehouse-quarantine-item-row-${itemId}-action-dropdown`}
                          ref={dropdownRef}
                          className="absolute right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right overflow-hidden"
                        >
                          <button
                            id={`warehouse-quarantine-item-row-${itemId}-action-view`}
                            onClick={() => handleActionClick("view", item)}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-xs hover:bg-gray-100 text-gray-700 transition-colors"
                          >
                            <Icon
                              path={mdiEye}
                              size={0.5}
                              className="opacity-70"
                            />
                            View Details
                          </button>
                          <button
                            id={`warehouse-quarantine-item-row-${itemId}-action-release`}
                            onClick={() => handleActionClick("release", item)}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-xs hover:bg-gray-100 text-success transition-colors"
                          >
                            <Icon
                              path={mdiFileCheckOutline}
                              size={0.5}
                              className="opacity-70"
                            />
                            Release to Stock
                          </button>
                          <button
                            id={`warehouse-quarantine-item-row-${itemId}-action-return`}
                            onClick={() => handleActionClick("return", item)}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-xs hover:bg-gray-100 text-info transition-colors"
                          >
                            <Icon
                              path={mdiUndoVariant}
                              size={0.5}
                              className="opacity-70"
                            />
                            Return to Vendor
                          </button>
                          <button
                            id={`warehouse-quarantine-item-row-${itemId}-action-scrap`}
                            onClick={() => handleActionClick("scrap", item)}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-xs hover:bg-gray-100 text-destructive transition-colors"
                          >
                            <Icon
                              path={mdiBlockHelper}
                              size={0.5}
                              className="opacity-70"
                            />
                            Scrap Item
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {isExpanded && item.disputed_device_ids && (
                    <tr className="bg-gray-50/50">
                      <td colSpan={11} className="p-4 pt-0">
                        <div className="pl-4 py-3 border-l-2 border-primary-100 ml-3">
                          <DeviceIdsViewer
                            deviceIds={item.disputed_device_ids}
                            title="Quarantined Device IDs"
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden p-4 pt-0 space-y-3">
        {items.map((item, index) => {
          const isFG = item.item_type === "Finished Goods";
          const itemId = item.id || `item-${index}`;
          const isExpanded = expandedRows[itemId];

          return (
            <MobileCard
              key={itemId}
              id={`warehouse-quarantine-item-card-${itemId}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 break-words">
                      {item.item_name || "N/A"}
                    </p>
                    {isFG && (
                      <span className="inline-flex items-center rounded-full border border-gray-200 px-2 py-0.5 text-xs font-semibold gap-1 bg-gray-50 shrink-0">
                        <Icon path={mdiCpu64Bit} size={0.5} />
                        FG
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 break-words">
                    {item.inventory_item_code}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium shrink-0 ${
                    statusStyles[item.status?.toLowerCase()] ||
                    "bg-gray-100 text-gray-700"
                  }`}
                >
                  {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                </span>
              </div>

              <div className="divide-y divide-gray-100">
                <MobileField label="Order">
                  <span className="font-mono text-xs text-gray-600">
                    {item.inward_order_id}
                  </span>
                </MobileField>
                <MobileField label="Warehouse">
                  {item.warehouse_name}
                </MobileField>
                <MobileField label="Disputed Qty">
                  {item.disputed_physical_quantity}
                </MobileField>
                <MobileField label="Disputed Rate">
                  {formatCurrency(item.disputed_invoice_rate || 0)}
                </MobileField>
                <MobileField label="Dispute Type">
                  <span className="flex flex-wrap justify-end gap-1">
                    {item.dispute_types.map((type, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-700"
                      >
                        {type}
                      </span>
                    ))}
                  </span>
                </MobileField>
                <MobileField label="Date">
                  {item.created_at
                    ? dayjs(item.created_at).format("DD MMM YYYY")
                    : "N/A"}
                </MobileField>
              </div>

              {isFG && (
                <button
                  id={`warehouse-quarantine-item-card-${itemId}-toggle-device-ids-button`}
                  onClick={() => toggleRow(itemId)}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 font-medium"
                >
                  <Icon
                    path={isExpanded ? mdiChevronDown : mdiChevronRight}
                    size={0.7}
                  />
                  {isExpanded ? "Hide" : "Show"} Device IDs
                </button>
              )}
              {isExpanded && item.disputed_device_ids && (
                <div className="mt-2 pl-3 border-l-2 border-primary-100">
                  <DeviceIdsViewer
                    deviceIds={item.disputed_device_ids}
                    title="Quarantined Device IDs"
                  />
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2 border-t pt-3">
                <button
                  id={`warehouse-quarantine-item-card-${itemId}-action-view`}
                  onClick={() => handleActionClick("view", item)}
                  className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100"
                >
                  <Icon path={mdiEye} size={0.5} />
                  View
                </button>
                <button
                  id={`warehouse-quarantine-item-card-${itemId}-action-release`}
                  onClick={() => handleActionClick("release", item)}
                  className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-success hover:bg-gray-100"
                >
                  <Icon path={mdiFileCheckOutline} size={0.5} />
                  Release
                </button>
                <button
                  id={`warehouse-quarantine-item-card-${itemId}-action-return`}
                  onClick={() => handleActionClick("return", item)}
                  className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-info hover:bg-gray-100"
                >
                  <Icon path={mdiUndoVariant} size={0.5} />
                  Return
                </button>
                <button
                  id={`warehouse-quarantine-item-card-${itemId}-action-scrap`}
                  onClick={() => handleActionClick("scrap", item)}
                  className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-destructive hover:bg-gray-100"
                >
                  <Icon path={mdiBlockHelper} size={0.5} />
                  Scrap
                </button>
              </div>
            </MobileCard>
          );
        })}
      </div>

      <QuarantineItemDetailsModal
        item={selectedItemForDetails}
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
      />
    </div>
  );
};

export default QuarantinedItemsList;
